import { isAxiosError } from "axios";
import { v4 } from "uuid";

import type { UserInput } from "@intelligems/ig-types/schemas/ai/node-execution";
import type { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { isAbortError } from "@intelligems/ig-utils/stream-utils";
import {
  getAgentSession,
  getLatestAgentSession,
  startAgentTurn,
  streamAgentSession,
} from "@src/api/ai/agents/agent-session";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";
import {
  type AgentStreamCursor,
  briefingRegionOf,
  savedStatusOf,
  SESSION_AT_REST,
  toUiStreamActions,
} from "@src/features/opportunityAudit/agentStream/toUiStreamActions";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import type { AppDispatch, RootState } from "@src/store/rootState";
import {
  atlasCursorMoved,
  atlasLookupFailed,
  atlasSessionStarted,
  atlasTurnSettled,
  atlasTurnStarted,
  auditReset,
  briefingRegionLanded,
  getAtlasConnection,
  getRecommendationKeys,
  merchantTyped,
} from "@src/store/slices/opportunityAudit";
import {
  auditChatAction,
  auditChatCleared,
} from "@src/store/slices/opportunityAudit/chat";
import {
  loadRecommendationStatuses,
  statusFromProgress,
} from "@src/store/slices/opportunityAudit/thunks/recommendationStatuses";
import { trackAuditOpened } from "@src/store/slices/opportunityAudit/thunks/tracking/trackAuditOpened";

const AGENT_NAME = "opportunity-audit";

/**
 * What opens the conversation before the merchant has typed anything.
 *
 * The agent declares `inputIsUserMessage`, so an empty input would reach the
 * model as an empty text block. The `<system>` tags are the convention for
 * frontend-injected turns, and the panel hides them from the transcript.
 */
const OPENING_INPUT =
  "<system>The merchant has just opened the audit and has not said anything yet.</system>";

/** Reconnects in a row that bring nothing, before the turn is given up. */
const EMPTY_RECONNECT_LIMIT = 3;
const RECONNECT_DELAY_MS = 250;

const UNREACHABLE = "Atlas could not be reached. Try again in a moment.";

export interface AtlasAgentApi {
  start: typeof startAgentTurn;
  stream: typeof streamAgentSession;
  latest: typeof getLatestAgentSession;
  getSession: typeof getAgentSession;
}

interface ThunkTools {
  dispatch: AppDispatch;
  getState: () => RootState;
}

/** The turn a 409 says is already running in the session, if that is what it is. */
const activeTurnOf = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }
  const data = error.response.data as {
    error?: unknown;
    activeTurn?: { turnId?: unknown };
  };
  return data?.error === "session_has_active_turn" &&
    typeof data.activeTurn?.turnId === "string"
    ? data.activeTurn.turnId
    : null;
};

/**
 * The audit's conversation with the agent. A turn is two calls: `start` accepts
 * it, then the stream carries everything it produces.
 *
 * The open connection is the one thing kept outside the store, because an
 * `AbortController` is not state. Every lookup, replay and turn runs on it, so
 * a reset aborts whichever is open. It is not closed when the tab unmounts:
 * the turn keeps filling the slice, so a merchant who comes back finds it done.
 */
/** The turn statuses web/ai may still be running. */
const ACTIVE_TURN_STATUSES = new Set([
  "queued",
  "running",
  "waiting_for_input",
]);

export const createAtlasAgent = (api: AtlasAgentApi) => {
  let connection: AbortController | null = null;

  /** Takes the connection, aborting whatever held it, and marks the panel pending. */
  const take = (dispatch: AppDispatch): AbortController => {
    connection?.abort();
    const abort = new AbortController();
    connection = abort;
    dispatch(atlasTurnStarted());
    return abort;
  };

  /** Lets go of the connection, unless something newer has taken it. */
  const release = (abort: AbortController, dispatch: AppDispatch) => {
    if (connection === abort) {
      connection = null;
      dispatch(atlasTurnSettled());
    }
  };

  const emit = (dispatch: AppDispatch, actions: UiStreamAction[]) => {
    if (actions.length > 0) {
      dispatch(auditChatAction(actions));
    }
  };

  const failed = (dispatch: AppDispatch) =>
    emit(dispatch, [
      {
        type: "setError",
        turnId: "atlas",
        nodeId: "opportunity-audit",
        nodeName: "Atlas",
        message: UNREACHABLE,
      },
      { type: "finish", turnId: "atlas" },
    ]);

  const onFrame = (frame: unknown, { dispatch, getState }: ThunkTools) => {
    const next = { ...getAtlasConnection(getState()).cursor };
    const actions = toUiStreamActions(frame, next);
    dispatch(atlasCursorMoved(next));
    emit(dispatch, actions);
    const record = savedStatusOf(frame, next.replaying);
    if (record) {
      void dispatch(statusFromProgress(record));
    }
    const region = briefingRegionOf(frame);
    if (region) {
      dispatch(briefingRegionLanded(region));
    }
  };

  /**
   * Streams the session until `ended` holds. The server closes the stream at
   * a turn's end, but also when its live buffer no longer holds our cursor,
   * and then it expects a reconnect. So the stream is reopened until it ends.
   * Rejects with an abort error when the connection is aborted.
   */
  const streamUntil = async (
    sessionId: string,
    ended: (cursor: AgentStreamCursor) => boolean,
    abort: AbortController,
    tools: ThunkTools
  ): Promise<void> => {
    const cursorNow = () => getAtlasConnection(tools.getState()).cursor;
    let empty = 0;
    for (;;) {
      // The socket can stay open long after the last frame, so the stream is
      // closed here once the end is seen.
      const done = new AbortController();
      const forward = () => done.abort();
      abort.signal.addEventListener("abort", forward);
      let frames = 0;
      try {
        const { afterSequence, afterStreamSeq } = cursorNow();
        await api.stream({
          sessionId,
          afterSequence,
          afterStreamSeq,
          signal: done.signal,
          onFrame: (frame) => {
            frames += 1;
            onFrame(frame, tools);
            if (ended(cursorNow())) {
              done.abort();
            }
          },
        });
      } catch (error) {
        if (!isAbortError(error)) {
          throw error;
        }
      } finally {
        abort.signal.removeEventListener("abort", forward);
      }
      if (abort.signal.aborted) {
        throw new DOMException("The connection was reset", "AbortError");
      }
      if (ended(cursorNow())) {
        return;
      }
      empty = frames === 0 ? empty + 1 : 0;
      if (empty >= EMPTY_RECONNECT_LIMIT) {
        throw new Error("The stream kept closing before the turn ended");
      }
      await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));
    }
  };

  const endsAt =
    (turnId: string) =>
    (cursor: AgentStreamCursor): boolean =>
      cursor.endedTurnId === turnId || cursor.endedTurnId === SESSION_AT_REST;

  /**
   * Starts one turn. With no session it starts one, which only `startAudit`
   * asks for. A turn already running in the session is followed to its end,
   * and this one is then started once more. Null when a reset came while it
   * was starting.
   */
  const begin = async (
    input: string,
    sessionId: string | undefined,
    abort: AbortController,
    tools: ThunkTools
  ) => {
    let started: Awaited<ReturnType<typeof api.start>>;
    try {
      started = await api.start({ agentName: AGENT_NAME, input, sessionId });
    } catch (error) {
      const running = activeTurnOf(error);
      if (running === null || sessionId === undefined) {
        throw error;
      }
      await streamUntil(sessionId, endsAt(running), abort, tools);
      started = await api.start({ agentName: AGENT_NAME, input, sessionId });
    }
    // A reset while the turn was starting: the session belongs to a
    // conversation that is gone.
    if (abort.signal.aborted) {
      return null;
    }
    if (sessionId === undefined) {
      tools.dispatch(atlasSessionStarted(started.sessionId));
      tools.dispatch(
        auditChatAction({ type: "setSessionId", sessionId: started.sessionId })
      );
    }
    return started;
  };

  /** Streams a started turn to its end. */
  const follow = (
    started: Awaited<ReturnType<typeof api.start>>,
    abort: AbortController,
    tools: ThunkTools
  ) => streamUntil(started.sessionId, endsAt(started.turnId), abort, tools);

  /** Runs `work` on the connection, and shows the failure if it fails. */
  const onConnection = async (
    tools: ThunkTools,
    work: (abort: AbortController) => Promise<void>
  ) => {
    const abort = take(tools.dispatch);
    try {
      await work(abort);
    } catch (error) {
      if (!isAbortError(error) && !abort.signal.aborted) {
        failed(tools.dispatch);
      }
    } finally {
      release(abort, tools.dispatch);
    }
  };

  /** Continues the session the panel holds. With none held it sends nothing. */
  const runTurn = createAsyncThunk<void, string>(
    "opportunityAudit/atlasTurn",
    async (input, tools) => {
      const { sessionId } = getAtlasConnection(tools.getState());
      if (sessionId === null) {
        return;
      }
      await onConnection(tools, async (abort) => {
        const started = await begin(input, sessionId, abort, tools);
        if (started !== null) {
          await follow(started, abort, tools);
        }
      });
    }
  );

  /**
   * The only call that starts a session: the opening, with no session id.
   * `opened` when the merchant's landing on the page asked for it.
   */
  const startAudit = createAsyncThunk<void, "opened" | "restarted">(
    "opportunityAudit/startAudit",
    async (reason, tools) => {
      await onConnection(tools, async (abort) => {
        const started = await begin(OPENING_INPUT, undefined, abort, tools);
        if (started === null) {
          return;
        }
        if (reason === "opened") {
          void tools.dispatch(trackAuditOpened("new"));
        }
        await follow(started, abort, tools);
      });
    }
  );

  /**
   * Draws a stored conversation, then reads what the panel needs beside it.
   * A turn still running is followed to its end, with Rex shown working.
   */
  const resume = async (
    snapshot: NonNullable<Awaited<ReturnType<typeof api.latest>>>,
    abort: AbortController,
    tools: ThunkTools
  ): Promise<boolean> => {
    const { dispatch, getState } = tools;
    const sessionId = snapshot.session.id;
    dispatch(atlasSessionStarted(sessionId));
    dispatch(auditChatAction({ type: "setSessionId", sessionId }));
    void dispatch(trackAuditOpened("resumed"));
    const cursor = getAtlasConnection(getState()).cursor;
    dispatch(atlasCursorMoved({ ...cursor, replaying: true }));
    try {
      await streamUntil(
        sessionId,
        endsAt(snapshot.currentTurn?.id ?? SESSION_AT_REST),
        abort,
        tools
      );
    } finally {
      const after = getAtlasConnection(getState()).cursor;
      dispatch(atlasCursorMoved({ ...after, replaying: false }));
    }
    // Read beside the conversation, so the panel is ready once it is drawn.
    void dispatch(
      loadRecommendationStatuses(getRecommendationKeys(getState()))
    );
    // A turn that had ended when the session was looked up cannot have changed
    // it since; only one still running is read again.
    const status = snapshot.currentTurn?.status;
    const session =
      status !== undefined && ACTIVE_TURN_STATUSES.has(status)
        ? (await api.getSession(sessionId)).session
        : snapshot.session;
    return session.state.briefed === true;
  };

  /**
   * Opens the audit: this user's latest one, or a new one when they have
   * none. Runs on every mount and every org change. With a session already
   * held, it reads the recommendations' statuses again.
   */
  const openAtlas = createAsyncThunk<void, void>(
    "opportunityAudit/openAtlas",
    async (_, tools) => {
      const { dispatch, getState } = tools;
      const { sessionId, pending } = getAtlasConnection(getState());
      if (pending) {
        return;
      }
      if (sessionId !== null) {
        void dispatch(trackAuditOpened("resumed"));
        void dispatch(
          loadRecommendationStatuses(getRecommendationKeys(getState()))
        );
        return;
      }
      const abort = take(dispatch);
      let opening: "none" | "new" | "brief" = "none";
      try {
        const latest = await api.latest(AGENT_NAME);
        if (abort.signal.aborted) {
          return;
        }
        if (latest === null) {
          opening = "new";
        } else if (!(await resume(latest, abort, tools))) {
          opening = "brief";
        }
      } catch (error) {
        // A failed lookup starts nothing, so a blip never makes a fresh
        // session the latest. The panel offers the lookup again.
        if (!isAbortError(error) && !abort.signal.aborted) {
          failed(dispatch);
          dispatch(atlasLookupFailed());
        }
      } finally {
        release(abort, dispatch);
      }
      if (opening === "new") {
        await dispatch(startAudit("opened"));
      } else if (opening === "brief") {
        await dispatch(runTurn(OPENING_INPUT));
      }
    }
  );

  /** The merchant's message: their bubble, then the turn that answers it. */
  const sendToAtlas = createAsyncThunk<void, UserInput>(
    "opportunityAudit/sendToAtlas",
    (input, { dispatch }) => {
      // The server echoes the message back with an id of its own, so the
      // bubble is added here and not from the stream.
      const { message } = createUserMessage(input);
      if (message) {
        dispatch(
          auditChatAction([
            {
              type: "addMessage",
              turnId: v4(),
              message,
              nodeId: v4(),
              nodeName: "User Input",
            },
          ])
        );
      }

      const text = utteranceFrom(input);
      if (text === null) {
        return;
      }
      if (input.type === "text") {
        dispatch(merchantTyped());
      }
      void dispatch(runTurn(text));
    }
  );

  /**
   * Ends the conversation. Everything it accumulated goes, or the next session
   * inherits it: the connection, the session, the cursor, the transcript and
   * the panel.
   */
  const resetAtlas = createAsyncThunk<void, void>(
    "opportunityAudit/resetAtlas",
    (_, { dispatch }) => {
      connection?.abort();
      connection = null;
      dispatch(auditChatCleared());
      dispatch(auditReset());
    }
  );

  /** "New audit": a fresh session, which becomes the latest. */
  const restartAtlas = createAsyncThunk<void, void>(
    "opportunityAudit/restartAtlas",
    async (_, { dispatch }) => {
      await dispatch(resetAtlas());
      await dispatch(startAudit("restarted"));
    }
  );

  return { openAtlas, sendToAtlas, resetAtlas, restartAtlas };
};

/**
 * The merchant's message as the agent takes it: a plain string. Null for the
 * arms the audit's composer cannot produce.
 */
const utteranceFrom = (input: UserInput): string | null => {
  switch (input.type) {
    case "text":
      return input.content.trim() === "" ? null : input.content;
    case "choice":
      return input.choice;
    case "confirmation":
      return input.confirmed ? "Yes" : "No";
    case "file":
    case "multi-choice":
    case "survey":
    case "answers":
    case "tool-result":
      return null;
  }
};

export const { openAtlas, sendToAtlas, resetAtlas, restartAtlas } =
  createAtlasAgent({
    start: startAgentTurn,
    stream: streamAgentSession,
    latest: getLatestAgentSession,
    getSession: getAgentSession,
  });
